import React, { useRef, Suspense, lazy } from 'react';
import { useIntl } from 'react-intl-next';
import isEqual from 'lodash/isEqual';

import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { CollabEventConflictPayload } from '@atlaskit/editor-common/collab';

import { fg } from '@confluence/feature-gating';

import type { ConflictResolutionUIPlugin } from './conflictResolutionUIPluginType.ts';
import { showConflictsResolvedFlag } from './ui/conflictsResolvedFlag.tsx';
import { createPlugin } from './pm-plugins/main.ts';
import { conflictResolutionPluginKey } from './pm-plugins/pluginKey.ts';
import { setMetadata, navigateToAvailable } from './pm-plugins/pluginState.ts';

const LazyContentComponent = lazy(async () => ({
	default: (
		await import(
			/* webpackChunkName: "loadable-content-resolution-content-component" */ './ui/contentComponent.tsx'
		)
	).ContentComponent,
}));

const ContentComponentWrapper = ({
	api,
	config,
	editorView,
}: {
	api: any;
	config: any;
	editorView: any;
}) => {
	const intl = useIntl();
	const lastConflictCount = useRef<number>(0);

	// Watch for conflicts being resolved. This is so we can show the success flag.
	usePluginStateEffect(api, ['conflictResolutionUI'], ({ conflictResolutionUIState }) => {
		if (!fg('platform_editor_offline_editing_web_ga_fixes')) {
			return;
		}

		if (!config?.flags) {
			return;
		}

		if (!config?.contentId || !config?.spaceKey) {
			return;
		}

		const currentConflictCount =
			(conflictResolutionUIState?.conflicts?.inserted?.length ?? 0) +
			(conflictResolutionUIState?.conflicts?.deleted?.length ?? 0);

		// If we had conflicts and now we don't, show success flag
		if (lastConflictCount.current > 0 && currentConflictCount === 0) {
			showConflictsResolvedFlag(config.flags, intl, config.contentId, config.spaceKey);
		}

		lastConflictCount.current = currentConflictCount;
	});

	return (
		<Suspense fallback={null}>
			<LazyContentComponent api={api} editorView={editorView} />
		</Suspense>
	);
};

export const conflictResolutionUIPlugin: ConflictResolutionUIPlugin = ({ api, config }) => {
	return {
		name: 'conflictResolutionUI',
		contentComponent: ({ editorView }) => {
			if (!editorView) {
				return null;
			}

			return <ContentComponentWrapper api={api} config={config} editorView={editorView} />;
		},
		getSharedState: (state) => {
			if (!state) {
				return undefined;
			}
			return {
				selected: conflictResolutionPluginKey.getState(state)?.selected,
				isNextEnabled: conflictResolutionPluginKey.getState(state)?.isNextEnabled ?? true,
				isPreviousEnabled: conflictResolutionPluginKey.getState(state)?.isPreviousEnabled ?? true,
				conflicts: conflictResolutionPluginKey.getState(state)?.conflicts,
				resolutionMode: conflictResolutionPluginKey.getState(state)?.resolutionMode ?? 'viewing',
			};
		},
		commands: {
			navigateToFirstConflict: ({ tr }) => {
				const conflicts = api?.conflictResolutionUI?.sharedState.currentState()?.conflicts;

				if (conflicts && (conflicts.inserted.length > 0 || conflicts?.deleted.length > 0)) {
					return navigateToAvailable(undefined, tr, conflicts);
				}
				return null;
			},
			startResolvingConflicts: ({ tr }) => {
				const conflicts = api?.conflictResolutionUI?.sharedState.currentState()?.conflicts;

				// Early exit when we've no conflicts to resolve.
				if (!conflicts || (conflicts.inserted.length === 0 && conflicts.deleted.length === 0)) {
					return null;
				}

				// First, start resolving mode
				const trWithMode = tr.setMeta(conflictResolutionPluginKey, { type: 'startResolving' });

				// Then navigate to first conflict
				const trWithNavigation = navigateToAvailable(undefined, trWithMode, conflicts);

				return trWithNavigation || trWithMode;
			},
		},
		pmPlugins: () => {
			return [
				{
					name: 'conflictResolutionPlugin',
					plugin: createPlugin,
				},
			];
		},
		usePluginHook: ({ editorView }) => {
			const lastReconnectionConflictMetadataRef = useRef<CollabEventConflictPayload | undefined>();

			usePluginStateEffect(api, ['collabEdit'], ({ collabEditState }) => {
				const { lastReconnectionConflictMetadata } = collabEditState ?? {};
				if (
					lastReconnectionConflictMetadata &&
					!isEqual(lastReconnectionConflictMetadataRef.current, lastReconnectionConflictMetadata)
				) {
					setMetadata(
						editorView,
						{
							type: 'update',
							data: lastReconnectionConflictMetadata,
						},
						{ api },
					);
					lastReconnectionConflictMetadataRef.current = lastReconnectionConflictMetadata;
				}
			});
		},
	};
};
